// Sistema de cache inteligente persistente sobre localStorage.
// Implementa cache con TTL y manejo de cuotas excedidas.
// Reduce llamadas API en 50-70% según métricas del plan.
// Basado en implementación de Alex optimizada para el sistema actual.

const PREFIX = 'amarsi-cache:';

// Configuración por defecto
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutos
const MAX_CACHE_SIZE = 50; // Máximo de items en cache

export const cache = {
  /**
   * Obtiene un valor del cache.
   * @param {string} key - Clave del cache
   * @returns {any|null} Valor cacheado o null si no existe/expiró
   */
  get(key) {
    try {
      const raw = localStorage.getItem(PREFIX + key);
      if (!raw) return null;

      const cached = JSON.parse(raw);
      
      // Verificar TTL
      if (cached.ttl && Date.now() > cached.ttl) {
        this.remove(key);
        return null;
      }

      console.log(`[CACHE] Hit para key: ${key}`);
      return cached.data;
    } catch (error) {
      console.warn(`[CACHE] Error obteniendo key ${key}:`, error);
      this.remove(key);
      return null;
    }
  },

  /**
   * Guarda un valor en el cache con TTL opcional.
   * @param {string} key - Clave del cache
   * @param {any} value - Valor a cachear
   * @param {number} ttl - Time to live en ms (opcional)
   */
  set(key, value, ttl = DEFAULT_TTL) {
    const cacheItem = {
      data: value,
      timestamp: Date.now(),
      ttl: ttl ? Date.now() + ttl : null
    };

    try {
      localStorage.setItem(PREFIX + key, JSON.stringify(cacheItem));
      
      // Limpiar cache si excede el tamaño máximo
      this.cleanup();
      
      console.log(`[CACHE] Set para key: ${key}, TTL: ${ttl}ms`);
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('[CACHE] Cuota de localStorage excedida, limpiando cache antiguo');
        this.cleanup(true); // Forzar limpieza agresiva
        // Reintentar una vez después de limpieza
        try {
          localStorage.setItem(PREFIX + key, JSON.stringify(cacheItem));
        } catch (retryError) {
          console.error('[CACHE] No se pudo guardar después de limpieza:', retryError);
        }
      } else {
        console.error(`[CACHE] Error guardando key ${key}:`, error);
      }
    }
  },

  /**
   * Elimina un item específico del cache.
   * @param {string} key - Clave a eliminar
   */
  remove(key) {
    try {
      localStorage.removeItem(PREFIX + key);
      console.log(`[CACHE] Remove para key: ${key}`);
    } catch (error) {
      console.error(`[CACHE] Error eliminando key ${key}:`, error);
    }
  },

  /**
   * Limpia items expirados o antiguos del cache.
   * @param {boolean} aggressive - Si es true, limpia más agresivamente
   */
  cleanup(aggressive = false) {
    try {
      const keysToRemove = [];
      const now = Date.now();
      
      // Recorrer todos los items del cache
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(PREFIX)) {
          try {
            const raw = localStorage.getItem(key);
            if (raw) {
              const cached = JSON.parse(raw);
              const cacheKey = key.replace(PREFIX, '');
              
              // Remover si expiró
              if (cached.ttl && now > cached.ttl) {
                keysToRemove.push(cacheKey);
              }
              
              // Si es agresivo, remover items muy antiguos (más de 30 min)
              if (aggressive && cached.timestamp && (now - cached.timestamp) > 30 * 60 * 1000) {
                keysToRemove.push(cacheKey);
              }
            }
          } catch (parseError) {
            // Remover items corruptos
            keysToRemove.push(key.replace(PREFIX, ''));
          }
        }
      }
      
      // Eliminar keys marcados
      keysToRemove.forEach(key => this.remove(key));
      
      // Si todavía hay demasiados items, remover los más antiguos
      if (!aggressive) {
        const remainingKeys = this.getAllKeys();
        if (remainingKeys.length > MAX_CACHE_SIZE) {
          const sortedKeys = remainingKeys
            .map(key => ({
              key,
              timestamp: this.getTimestamp(key)
            }))
            .sort((a, b) => a.timestamp - b.timestamp);
          
          // Eliminar los más antiguos hasta quedar dentro del límite
          const toRemove = sortedKeys.slice(0, remainingKeys.length - MAX_CACHE_SIZE);
          toRemove.forEach(({ key }) => this.remove(key));
        }
      }
      
      console.log(`[CACHE] Cleanup completado. Removidos: ${keysToRemove.length} items`);
    } catch (error) {
      console.error('[CACHE] Error en cleanup:', error);
    }
  },

  /**
   * Obtiene todas las keys del cache (sin prefijo).
   * @returns {string[]} Array de keys
   */
  getAllKeys() {
    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(PREFIX)) {
          keys.push(key.replace(PREFIX, ''));
        }
      }
      return keys;
    } catch (error) {
      console.error('[CACHE] Error obteniendo keys:', error);
      return [];
    }
  },

  /**
   * Obtiene el timestamp de un item cacheado.
   * @param {string} key - Clave del cache
   * @returns {number|null} Timestamp o null
   */
  getTimestamp(key) {
    try {
      const raw = localStorage.getItem(PREFIX + key);
      if (!raw) return null;

      const cached = JSON.parse(raw);
      return cached.timestamp || null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Limpia todo el cache de la aplicación.
   */
  clear() {
    try {
      const keys = this.getAllKeys();
      keys.forEach(key => this.remove(key));
      console.log(`[CACHE] Cache limpiado completamente. ${keys.length} items removidos`);
    } catch (error) {
      console.error('[CACHE] Error limpiando cache:', error);
    }
  },

  // Métodos para integración con TanStack Query
  /**
   * Obtiene datos de query cacheados.
   * @param {Array} queryKey - Array de clave de query
   * @returns {any|null} Datos cacheados o null
   */
  getQueryData(queryKey) {
    const cacheKey = `query:${JSON.stringify(queryKey)}`;
    return this.get(cacheKey);
  },

  /**
   * Guarda datos de query en cache.
   * @param {Array} queryKey - Array de clave de query
   * @param {any} data - Datos a cachear
   * @param {number} ttl - TTL personalizado (opcional)
   */
  setQueryData(queryKey, data, ttl) {
    const cacheKey = `query:${JSON.stringify(queryKey)}`;
    this.set(cacheKey, data, ttl);
  },

  /**
   * Invalida datos de query específicos.
   * @param {Array} queryKey - Array de clave de query
   */
  invalidateQuery(queryKey) {
    const cacheKey = `query:${JSON.stringify(queryKey)}`;
    this.remove(cacheKey);
  },

  /**
   * Obtiene estadísticas del cache para debugging.
   * @returns {Object} Estadísticas del cache
   */
  getStats() {
    try {
      const keys = this.getAllKeys();
      const now = Date.now();
      let expiredCount = 0;
      let totalSize = 0;

      keys.forEach(key => {
        const raw = localStorage.getItem(PREFIX + key);
        if (raw) {
          totalSize += raw.length;
          try {
            const cached = JSON.parse(raw);
            if (cached.ttl && now > cached.ttl) {
              expiredCount++;
            }
          } catch (e) {
            // Item corrupto
          }
        }
      });

      return {
        totalItems: keys.length,
        expiredItems: expiredCount,
        totalSize: totalSize,
        sizeFormatted: `${(totalSize / 1024).toFixed(2)} KB`
      };
    } catch (error) {
      console.error('[CACHE] Error obteniendo estadísticas:', error);
      return {
        totalItems: 0,
        expiredItems: 0,
        totalSize: 0,
        sizeFormatted: '0 KB'
      };
    }
  }
};

// Cleanup automático cada 10 minutos
setInterval(() => {
  cache.cleanup();
}, 10 * 60 * 1000);

// Cleanup al iniciar la aplicación
setTimeout(() => {
  cache.cleanup();
}, 1000);
